import React from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import type { Customer } from '../../../types/backend';
import type { useAccountForm } from '../hooks/useAccountForm';
import AccountOverviewTab from './AccountOverviewTab';
import AccountAccessTab from './AccountAccessTab';
import AccountRegionsTab from './AccountRegionsTab';
import AccountOnboardingPanel from './AccountOnboardingPanel';
import AccountBaselinesPanel from './AccountBaselinesPanel';

type TabKey = 'overview' | 'access' | 'regions' | 'baselines';

export interface AccountDetailTabsProps {
  customer: Customer;
  form: ReturnType<typeof useAccountForm>;
  baselineEnabled: boolean;
  /** customers:UpdateAWSAccount's level (L2 and up): the region edits. */
  allowAccountEdit: boolean;
  /** Admin: the optional-baseline switch and Run now. */
  allowAccountDecisions: boolean;
  /** The account form, shown on Overview under the facts. */
  formFields: React.ReactNode;
}

/**
 * One saved account in four tabs. The parent keys this by account, so opening
 * another account starts again on Overview.
 */
const AccountDetailTabs: React.FC<AccountDetailTabsProps> = ({
  customer, form, baselineEnabled, allowAccountEdit, allowAccountDecisions, formFields,
}) => {
  const [tab, setTab] = React.useState<TabKey>('overview');
  const account = form.selectedEntity;
  if (!account) return null;

  // Every account_baseline read is refused while the switch is off, so the
  // tab that is nothing but those reads goes with it.
  const tabs: Array<[TabKey, string]> = [
    ['overview', 'Overview'],
    ['access', 'Access'],
    ['regions', 'Regions'],
    ...(baselineEnabled ? [['baselines', 'Baselines'] as [TabKey, string]] : []),
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: { xs: 0, sm: 1 }, flexShrink: 0 }}>
        <Tabs
          value={tab}
          onChange={(_, v: TabKey) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Account sections"
        >
          {tabs.map(([key, label]) => (
            <Tab key={key} value={key} label={label} id={`account-tab-${key}`} aria-controls={`account-tabpanel-${key}`} sx={{ minHeight: 48 }} />
          ))}
        </Tabs>
      </Box>
      <Box
        role="tabpanel"
        id={`account-tabpanel-${tab}`}
        aria-labelledby={`account-tab-${tab}`}
        sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
      >
        {tab === 'overview' && (
          <AccountOverviewTab customer={customer} account={account} baselineEnabled={baselineEnabled}>
            {formFields}
          </AccountOverviewTab>
        )}
        {tab === 'access' && (
          <AccountAccessTab customer={customer} account={account} baselineEnabled={baselineEnabled} />
        )}
        {tab === 'regions' && <AccountRegionsTab form={form} canEdit={allowAccountEdit} />}
        {tab === 'baselines' && baselineEnabled && (
          <>
            <AccountBaselinesPanel
              customerId={customer.id}
              awsAccountId={account.aws_account_id}
              allowActions={allowAccountDecisions}
            />
            <AccountOnboardingPanel
              customerId={customer.id}
              awsAccountId={account.aws_account_id}
              enabled={account.enabled}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default AccountDetailTabs;
